import React, {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
    Alert,
    Button,
    Table
} from "react-bootstrap";

import Loading from "../../../components/Loading";
import ShopsContext from "../../../context/ShopsContext";
import CourseLessonOpeningShopUsersModal from "./modal/CourseLessonOpeningShopUsersModal";
import CourseOpeningSummaryShopUsersModal from "./modal/CourseOpeningSummaryShopUsersModal";

function getLessonOpeningPercentage(lesson, amountOfShopUsersThatStartedCourse) {
    const parsedOpeningPercentage = typeof lesson?.openingPercentage === "number"
        ? lesson.openingPercentage
        : parseFloat(lesson?.openingPercentage);

    if(Number.isFinite(parsedOpeningPercentage)) {
        return parsedOpeningPercentage;
    }
    if(!amountOfShopUsersThatStartedCourse) {
        return 0;
    }
    return ((lesson?.openedAmount || 0) / amountOfShopUsersThatStartedCourse) * 100;
}

function formatPercentage(percentage) {
    const normalizedPercentage = percentage < 0 ? 0 : percentage;
    const formattedPercentage = normalizedPercentage % 1 === 0
        ? normalizedPercentage.toFixed(0)
        : normalizedPercentage.toFixed(1);

    return formattedPercentage.replace(".", ",") + "%";
}

function CourseDetailStatistics({ course }) {
    const shopsContext = useContext(ShopsContext);
    const [courseStatistics, setCourseStatistics] = useState(null);
    const [error, setError] = useState(null);
    const [selectedLesson, setSelectedLesson] = useState(null);
    const [showLessonOpeningShopUsersModal, setShowLessonOpeningShopUsersModal] = useState(false);
    const [selectedSummaryGroup, setSelectedSummaryGroup] = useState(null);
    const [showCourseOpeningSummaryShopUsersModal, setShowCourseOpeningSummaryShopUsersModal] = useState(false);

    useEffect(() => {
        let cancelled = false;
        if(!course) {
            setCourseStatistics(null);
            setError(null);
            return () => {
                cancelled = true;
            };
        }
        const loadCourseStatistics = async () => {
            setCourseStatistics(null);
            setError(null);
            try {
                const response = await axios.post("/getCourseStatistics", {
                    shopId: shopsContext.currentShop.id,
                    courseId: course.id
                });

                if(cancelled) {
                    return;
                }

                if(!response.data.valid) {
                    setError("Er ging iets fout. Probeer het later opnieuw. (" + response.data.error + ")");
                    return;
                }
                setCourseStatistics(response.data.courseStatistics);
            } catch(requestError) {
                if(cancelled) {
                    return;
                }
                console.error(requestError);
                setError("Er ging iets fout. Probeer het later opnieuw.");
            }
        };
        loadCourseStatistics();
        return () => {
            cancelled = true;
        };
    }, [course, shopsContext.currentShop.id]);

    const handleLessonModalClose = useCallback(() => {
        setShowLessonOpeningShopUsersModal(false);
        setSelectedLesson(null);
    }, []);
    const openLessonModal = useCallback((lesson) => {
        setSelectedLesson(lesson);
        setShowLessonOpeningShopUsersModal(true);
    }, []);
    const handleSummaryModalClose = useCallback(() => {
        setShowCourseOpeningSummaryShopUsersModal(false);
        setSelectedSummaryGroup(null);
    }, []);
    const openSummaryModal = useCallback((summaryGroup) => {
        setSelectedSummaryGroup(summaryGroup);
        setShowCourseOpeningSummaryShopUsersModal(true);
    }, []);

    if(error) {
        return (
            <Alert variant="danger">{ error }</Alert>
        );
    }
    if(!courseStatistics) {
        return (
            <Loading/>
        );
    }

    const chapters = courseStatistics.chapters || [];
    const amountOfLessons = chapters.reduce((totalAmountOfLessons, chapter) => {
        return totalAmountOfLessons + (chapter.lessons || []).length;
    }, 0);
    const amountOfShopUsersThatStartedCourse = courseStatistics.amountOfShopUsersThatStartedCourse || 0;
    const amountOfShopUsersThatNeverStartedCourse = courseStatistics.amountOfShopUsersThatNeverStartedCourse || 0;
    const amountOfShopUsersThatEverHadAccess = courseStatistics.amountOfShopUsersThatEverHadAccess || 0;

    return (
        <React.Fragment>
            <CourseOpeningSummaryShopUsersModal
                show={ showCourseOpeningSummaryShopUsersModal }
                handleClose={ handleSummaryModalClose }
                course={ course }
                initialSection={ selectedSummaryGroup }
            />
            <CourseLessonOpeningShopUsersModal
                show={ showLessonOpeningShopUsersModal }
                handleClose={ handleLessonModalClose }
                lesson={ selectedLesson }
            />

            <div className="card course-statistics-summary mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-4 mb-3 mb-md-0">
                            <div className="course-statistics-summary__item">
                                <small className="text-muted d-block">Toegang gehad</small>
                                <strong className="course-statistics-summary__count">{ amountOfShopUsersThatEverHadAccess }</strong>
                            </div>
                        </div>
                        <div className="col-md-4 mb-3 mb-md-0">
                            <button
                                type="button"
                                className="course-statistics-summary__button"
                                onClick={ () => openSummaryModal("started") }
                            >
                                <div className="course-statistics-summary__item">
                                    <small className="text-muted d-block">Gestart met cursus</small>
                                    <strong className="course-statistics-summary__count">{ amountOfShopUsersThatStartedCourse }</strong>
                                </div>
                            </button>
                        </div>
                        <div className="col-md-4">
                            <button
                                type="button"
                                className="course-statistics-summary__button"
                                onClick={ () => openSummaryModal("never-started") }
                            >
                                <div className={ "course-statistics-summary__item" + (amountOfShopUsersThatNeverStartedCourse > 0 ? " course-statistics-summary__item--warning" : "") }>
                                    <small className="text-muted d-block">Nooit gestart</small>
                                    <strong className="course-statistics-summary__count">{ amountOfShopUsersThatNeverStartedCourse }</strong>
                                </div>
                            </button>
                        </div>
                    </div>

                    <p className="text-muted mb-0 mt-3">
                        Nooit gestart betekent: gebruiker had wel toegang tot deze cursus, maar opende nog geen enkele les.
                        Klik op Gestart met cursus of Nooit gestart om de gebruikers te bekijken.
                    </p>
                </div>
            </div>

            { amountOfShopUsersThatStartedCourse === 0 ? (
                <Alert variant="info">
                    Nog niemand is met deze cursus gestart. Lespercentages staan daarom overal op 0%.
                </Alert>
            ) : (
                <p className="text-muted mb-4">
                    Lespercentages zijn alleen gebaseerd op gebruikers die met de cursus gestart zijn.
                    Gebruikers die nooit een les in deze cursus openden tellen niet mee.
                </p>
            )}

            { amountOfLessons === 0 ? (
                <div className="d-flex flex-column align-items-center text-center">
                    <h1><i className="fas fa-chart-column"/></h1>
                    <h4>Geen lessen</h4>
                    <p className="mb-0">Deze cursus heeft nog geen lessen om statistieken voor te tonen.</p>
                </div>
            ) : chapters.map((chapter) => (
                <div className="card mb-3" key={ chapter.id }>
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <b>{ chapter.title }</b>
                        { !chapter.enabled && (
                            <small className="text-muted">Hoofdstuk uitgeschakeld</small>
                        )}
                    </div>
                    { (chapter.lessons || []).length === 0 ? (
                        <div className="card-body">
                            <span className="text-muted">Geen lessen in dit hoofdstuk.</span>
                        </div>
                    ) : (
                        <Table size="sm" hover className="mb-0">
                            <thead className="thead-light">
                            <tr>
                                <th className="text-left">Les</th>
                                <th className="text-center">Les geopend</th>
                                <th className="text-center">Les niet geopend</th>
                                <th className="text-right">Openingspercentage</th>
                            </tr>
                            </thead>
                            <tbody>
                            { (chapter.lessons || []).map((lesson) => {
                                const openingPercentage = getLessonOpeningPercentage(
                                    lesson,
                                    amountOfShopUsersThatStartedCourse
                                );
                                return (
                                    <tr key={ lesson.id }>
                                        <td className="text-left">
                                            <div>{ lesson.title }</div>
                                            { (!lesson.enabled || !lesson.visible) && (
                                                <small className="text-muted">
                                                    { !lesson.enabled ? "Uitgeschakeld" : "" }
                                                    { !lesson.enabled && !lesson.visible ? " • " : "" }
                                                    { !lesson.visible ? "Niet zichtbaar" : "" }
                                                </small>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            { lesson.openedAmount || 0 }
                                        </td>
                                        <td className="text-center">
                                            { lesson.notOpenedAmount || 0 }
                                        </td>
                                        <td className="text-right">
                                            <Button
                                                variant="link"
                                                className="p-0"
                                                onClick={ () => openLessonModal(lesson) }
                                            >
                                                { formatPercentage(openingPercentage) }
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </Table>
                    )}
                </div>
            ))}
        </React.Fragment>
    );
}
CourseDetailStatistics.propTypes = {
    course: PropTypes.object
}

export default React.memo(CourseDetailStatistics);
